import { useEffect, useMemo, useState } from "react";
import drillsData from "./data/drills.json";
import {
  type Constraints,
  type DrillLibrary,
  type Profile,
  type SessionPlan,
  type SessionRecord,
  type SessionResult,
} from "./data/schema";
import { filterDrills, generateSession } from "./lib/planGenerator";
import {
  drillHistory,
  flushOutbox,
  loadProfiles,
  loadSessions,
  pendingCount,
  saveProfile,
  saveSession,
} from "./lib/store";
import { getLastProfile, setLastProfile } from "./lib/overrides";
import { ProfileEditor, ProfilePicker } from "./components/Profiles";
import { ConstraintsForm } from "./components/ConstraintsForm";
import { SessionView } from "./components/SessionView";
import { SessionSummary } from "./components/SessionSummary";
import { HistoryScreen } from "./components/HistoryScreen";
import { VideoReview } from "./components/VideoReview";

const library = drillsData as DrillLibrary;

// Used only if the API + cache are both empty (e.g. first run offline).
const DEFAULT_PROFILES: Profile[] = [
  { id: "nathan", name: "Nathan", age: 12, position: "forward", focusAreas: ["finishing", "first-touch", "dribbling", "passing", "movement", "weak-foot"], updatedAt: "2026-09-10T00:00:00Z" },
  { id: "wyatt", name: "Wyatt", age: 14, position: "goalkeeper", focusAreas: ["shot-stopping", "handling", "footwork", "distribution", "crosses", "one-v-one"], updatedAt: "2026-09-10T00:00:00Z" },
];

type View =
  | "loading"
  | "profiles"
  | "edit"
  | "constraints"
  | "session"
  | "summary"
  | "history"
  | "videos";

declare const __APP_VERSION__: string;

export function App() {
  const [view, setView] = useState<View>("loading");
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [active, setActive] = useState<Profile | null>(null);
  const [plan, setPlan] = useState<SessionPlan | null>(null);
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [pending, setPending] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    void (async () => {
      const list = await loadProfiles();
      const resolved = list.length ? list : DEFAULT_PROFILES;
      setProfiles(resolved);
      await flushOutbox();
      setPending(pendingCount());
      setView("profiles");
    })();
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const selectProfile = async (p: Profile) => {
    setActive(p);
    setLastProfile(p.id);
    setSessions(await loadSessions(p.id));
    setView("constraints");
  };

  const generate = (c: Constraints) => {
    if (!active) return;
    setPlan(
      generateSession({
        profile: active,
        constraints: c,
        library,
        history: drillHistory(active.id),
      })
    );
    setView("session");
  };

  const swap = (index: number) => {
    if (!plan || !active) return;
    const target = plan.drills[index];
    const usedIds = new Set(plan.drills.map((d) => d.drill.id));
    const options = filterDrills(library.drills, active, plan.constraints).filter(
      (d) => d.block === target.block && !usedIds.has(d.id)
    );
    if (options.length === 0) return;
    const next = options[Math.floor(Math.random() * options.length)];
    const drills = [...plan.drills];
    drills[index] = { ...target, drill: next };
    setPlan({ ...plan, drills });
  };

  const finish = () => setView("summary");

  const saveAndClose = async (
    difficulty: 1 | 2 | 3 | 4 | 5,
    notes: string,
    results: SessionResult[]
  ) => {
    if (!plan || !active) return;
    const record: SessionRecord = {
      id: plan.id,
      profileId: active.id,
      date: plan.date,
      durationMin: plan.durationMin,
      plan,
      status: "completed",
      completedAt: new Date().toISOString(),
      perceivedDifficulty: difficulty,
      notes: notes || undefined,
      results,
    };
    await saveSession(record);
    setSessions(await loadSessions(active.id));
    setPending(pendingCount());
    setPlan(null);
    setView("profiles");
  };

  const saveProfileAndBack = async (p: Profile) => {
    await saveProfile(p);
    setProfiles((prev) => prev.map((x) => (x.id === p.id ? p : x)));
    setActive(p);
    setPending(pendingCount());
    setView("profiles");
  };

  // restore last profile label (cosmetic)
  useMemo(() => getLastProfile(), []);

  return (
    <div className="min-h-full">
      {(!online || pending > 0) && (
        <div className="sticky top-0 z-10 bg-[var(--color-amber)] px-4 py-1.5 text-center text-sm font-semibold text-[var(--color-ink)]">
          {online
            ? `${pending} change${pending === 1 ? "" : "s"} waiting to sync`
            : "Offline — plans still work, changes sync when you're back"}
        </div>
      )}

      {view === "loading" && (
        <div className="grid min-h-[70vh] place-items-center">
          <p className="h-display text-3xl text-[var(--color-pitch)]">Loading…</p>
        </div>
      )}

      {view === "profiles" && (
        <ProfilePicker
          profiles={profiles}
          onPick={selectProfile}
          onEdit={(p) => {
            setActive(p);
            setView("edit");
          }}
          onHistory={async (p) => {
            setActive(p);
            setSessions(await loadSessions(p.id));
            setView("history");
          }}
          onReviewVideos={() => setView("videos")}
        />
      )}

      {view === "edit" && active && (
        <ProfileEditor profile={active} onSave={saveProfileAndBack} onCancel={() => setView("profiles")} />
      )}

      {view === "constraints" && active && (
        <ConstraintsForm profile={active} onBack={() => setView("profiles")} onGenerate={generate} />
      )}

      {view === "session" && plan && active && (
        <SessionView
          plan={plan}
          profileName={active.name}
          onSwap={swap}
          onFinish={finish}
          onExit={() => {
            setPlan(null);
            setView("profiles");
          }}
        />
      )}

      {view === "summary" && plan && (
        <SessionSummary plan={plan} onSave={saveAndClose} onSkip={() => saveAndClose(3, "", [])} />
      )}

      {view === "history" && active && (
        <HistoryScreen profile={active} sessions={sessions} onBack={() => setView("profiles")} />
      )}

      {view === "videos" && <VideoReview library={library} onBack={() => setView("profiles")} />}

      <footer className="pb-[env(safe-area-inset-bottom)] pt-4 text-center text-xs text-neutral-400">
        Training v{typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev"} · a coach still beats an app — check their technique
      </footer>
    </div>
  );
}
